/**
 * A hover hint on an icon.
 *
 * HeroUI's tooltip waits 700 ms before it appears, which on a toolbar of
 * icon-only buttons reads as no tooltip at all — the pointer has moved on
 * by then. Everything in this app that needs a hint goes through here, so
 * the delay is one decision in one place rather than a prop every call site
 * has to remember. Every icon-only button carries one, beside its
 * `aria-label` (test/iconButtons.test.ts holds that).
 */
import { Tooltip } from '@heroui/react';
import type { ReactNode } from 'react';

/** Long enough not to flash while the pointer crosses a strip, short enough to feel like an answer. */
export const HINT_DELAY_MS = 250;

export function Hint({
  text,
  placement,
  children,
}: {
  text: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * The element that carries the hint: a HeroUI `Button` (or another React
   * Aria focusable). It takes the tooltip's trigger props straight from
   * context, so the button itself is the one tab stop — no `Tooltip.Trigger`
   * wrapper, which is a second focusable `div role="button"` around it with
   * no name of its own (roadmap U9).
   */
  children: ReactNode;
}) {
  return (
    <Tooltip delay={HINT_DELAY_MS}>
      {children}
      <Tooltip.Content placement={placement}>{text}</Tooltip.Content>
    </Tooltip>
  );
}
